/**
 * Dynamic Domain Configuration
 * Handles localhost, staging, and production domains with subdomain support
 */

/**
 * Get the base domain based on environment
 * Works with localhost:3000, localhost:3001, and production domains
 */
function getBaseDomain(): string {
  // Server-side: use environment variable
  if (typeof window === 'undefined') {
    const envDomain = process.env.NEXT_PUBLIC_SITE_URL;

    // If we have a valid non-localhost env var, use it
    if (envDomain && !envDomain.includes('localhost')) {
      return envDomain;
    }

    // Fallback logic
    if (process.env.NODE_ENV === 'production') {
      return 'https://sachinsharma.dev';
    }

    return envDomain || 'http://localhost:3000';
  }

  // Client-side: get from current window location
  const { hostname, port, protocol } = window.location;
  const portStr = port ? `:${port}` : '';

  return `${protocol}//${hostname}${portStr}`;
}

/**
 * Extract base hostname without subdomain
 * localhost:3000 -> localhost:3000
 * blogs.localhost:3000 -> localhost:3000
 * example.com -> example.com
 * blogs.example.com -> example.com
 */
function getBaseHostname(): string {
  const domain = getBaseDomain();
  const url = new URL(domain);
  const hostname = url.hostname;
  const port = url.port ? `:${url.port}` : '';

  // 1. Handle localhost cases (direct or with subdomain like blogs.localhost)
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return `localhost${port}`;
  }

  // 2. For production: remove the first subdomain if there are 3 or more parts
  // (e.g., blogs.sachinsharma.com -> sachinsharma.com)
  const parts = hostname.split('.');
  if (parts.length > 2) {
    // Check if it's not a common multi-part TLD (though rare for portfolio sites)
    // Basic logic: remove first part
    return parts.slice(1).join('.') + port;
  }

  return `${hostname}${port}`;
}

/**
 * Get subdomain URL dynamically
 * localhost:3000 -> http://blogs.localhost:3000
 * example.com -> https://blogs.example.com
 */
function getSubdomainUrl(subdomain: string): string {
  const domain = getBaseDomain();
  const url = new URL(domain);
  const baseHostname = getBaseHostname();

  return `${url.protocol}//${subdomain}.${baseHostname}`;
}

/**
 * Get main domain URL (removes any subdomain)
 */
function getMainDomainUrl(): string {
  const domain = getBaseDomain();
  const url = new URL(domain);
  const baseHostname = getBaseHostname();

  return `${url.protocol}//${baseHostname}`;
}

export const DYNAMIC_DOMAIN = {
  getBaseDomain,
  getBaseHostname,
  getSubdomainUrl,
  getMainDomainUrl,
};
